/**
 * Webinar « Formation clients — Communications ATNA » — two language-specific sessions.
 * The session shown follows the active site language (FR → French session, EN → English session).
 */

export type WebinarLang = 'FR' | 'EN'

export const WEBINAR_LOGOS = {
  bciti: 'https://cdn.magicpatterns.com/uploads/kFQruRR3oauw7NNUdgFajn/Logo_BCITI_(5).png',
  atna: 'https://cdn.magicpatterns.com/uploads/d6cqpG2SGUofNqC1hEedXv/Logo_ATNA_(5).png',
}

export type WebinarSession = {
  id: 'fr' | 'en'
  flag: 'fr' | 'gb'
  label: string
  date: string
  time: string
  /** Short line used by the alternative-session prompt. */
  altLine: string
  /** Announcement bar date (desktop / mobile). */
  barDate: string
  barShort: string
  cta: string
  startISO: string
  durationMinutes: number
  calendarTitle: string
  /** Value sent with the registration. */
  submissionLabel: string
}

export const webinarSessions: Record<WebinarLang, WebinarSession> = {
  FR: {
    id: 'fr',
    flag: 'fr',
    label: 'Session en français',
    date: 'Mercredi 14 octobre 2026',
    time: '9 h 30 – 10 h 30 HE',
    altLine: 'Session française · 9 h 30 HE',
    barDate: 'Mercredi 14 octobre · 9 h 30 HE',
    barShort: '14 oct. · 9 h 30 HE',
    cta: 'S’inscrire à la session française',
    startISO: '2026-10-14T09:30:00-04:00',
    durationMinutes: 60,
    calendarTitle: 'Formation clients — Communications ATNA (session française)',
    submissionLabel: 'FR — Mercredi 14 octobre 2026, 9 h 30 – 10 h 30 HE',
  },
  EN: {
    id: 'en',
    flag: 'gb',
    label: 'English session',
    date: 'Wednesday, October 14, 2026',
    time: '11:30 AM – 12:30 PM ET',
    altLine: 'English session · 11:30 AM ET',
    barDate: 'Wednesday, October 14 · 11:30 AM ET',
    barShort: 'Oct 14 · 11:30 AM ET',
    cta: 'Register for the English session',
    startISO: '2026-10-14T11:30:00-04:00',
    durationMinutes: 60,
    calendarTitle: 'Client Training — Communications ATNA (English session)',
    submissionLabel: 'EN — Wednesday, October 14, 2026, 11:30 AM – 12:30 PM ET',
  },
}

export type LearnIcon = 'target' | 'template' | 'atna' | 'analytics'

const FR = {
  nav: {
    aria: 'Navigation de la formation',
    home: 'BCITI | ATNA — Accueil',
    links: [
      { href: '#apprendre', label: 'Contenu' },
      { href: '#demonstration', label: 'Démonstration' },
      { href: '#programme', label: 'Programme' },
    ],
    register: 'S’inscrire',
    langLabel: 'Langue',
  },
  hero: {
    eyebrow: 'Formation clients | Communications ATNA',
    titleLead: 'Maîtrisez le nouveau',
    titleAccent: 'Communications ATNA',
    promise: 'Les bonnes personnes. Le bon message. Au bon moment.',
    body: 'Participez à une formation pratique de 60 minutes pour découvrir la nouvelle expérience Communications ATNA et apprendre à mieux cibler, préparer, diffuser et mesurer vos communications citoyennes.',
    micro: 'Formation réservée aux clients BCITI | ATNA',
    duration: '60 minutes',
    format: 'En ligne',
    altQuestion: 'Vous préférez participer en anglais?',
    altLink: 'Voir cette session',
  },
  /* Hero visual — illustrative figures only. */
  commandCenter: {
    module: 'Communications ATNA',
    status: 'Brouillon',
    title: 'Travaux – Secteur 3',
    stepsAria: 'Étapes de la communication',
    steps: ['Audience', 'Message', 'Canaux', 'Résultats'],
    audience: ['Secteur 3', 'Citoyens abonnés aux travaux'],
    audienceCount: '1 842 contacts',
    message: 'Des travaux sont en cours dans le secteur 3. La circulation sera déviée jusqu’à 18 h.',
    channels: ['Push', 'SMS', 'Courriel', 'Voix'],
    results: [
      { label: 'Ciblés', value: '1 842' },
      { label: 'Rejoints', value: '1 694' },
      { label: 'Non rejoints', value: '148' },
    ],
    illustrative: 'Données illustratives',
  },
  learn: {
    title: 'En 60 minutes, apprenez à tirer pleinement parti de Communications ATNA.',
    cta: 'S’inscrire à la formation',
    cards: [
      {
        icon: 'target' as LearnIcon,
        title: 'Ciblez les bonnes personnes',
        body: 'Créez des audiences selon les secteurs et quartiers, groupes d’intérêts, abonnements, propriétés, services, préférences et autres données autorisées disponibles dans votre environnement.',
      },
      {
        icon: 'template' as LearnIcon,
        title: 'Communiquez plus rapidement',
        body: 'Utilisez des modèles de communication préapprouvés selon vos sujets, vos équipes et vos règles d’affaires afin de standardiser et accélérer vos communications.',
      },
      {
        icon: 'atna' as LearnIcon,
        title: 'Travaillez avec ATNA',
        badge: 'Option forfait ATNA',
        body: 'Décrivez simplement la situation. ATNA peut vous assister pour préparer et améliorer le message, proposer l’audience appropriée, adapter le contenu selon les canaux et préparer la communication pour validation.',
      },
      {
        icon: 'analytics' as LearnIcon,
        title: 'Mesurez ce qui se passe',
        body: 'Suivez vos audiences, la livraison de vos communications et la performance des différents canaux à partir d’une vue analytique consolidée.',
      },
    ] as { icon: LearnIcon; title: string; body: string; badge?: string }[],
  },
  demo: {
    eyebrow: 'Démonstration en direct',
    title: 'De la situation terrain à la communication citoyenne.',
    withAtna: 'Avec ATNA',
    prompt: 'Des travaux imprévus touchent le secteur 3 jusqu’à 18 h. Prépare la communication aux citoyens concernés.',
    quote: ['« ', ' »'],
    preparing: 'ATNA prépare la communication',
    governance: 'ATNA prépare. Votre équipe valide et autorise l’envoi, selon vos règles d’approbation.',
    steps: [
      { label: 'Situation', text: '« Travaux imprévus dans le secteur 3 »' },
      { label: 'Cibler', text: 'Identifier les citoyens concernés' },
      { label: 'Préparer', text: 'Utiliser le bon modèle et adapter le message' },
      { label: 'Diffuser', text: 'Push · SMS · Courriel · Voix' },
      { label: 'Mesurer', text: 'Voir qui a été ciblé, rejoint ou non rejoint' },
    ],
    prepared: [
      'Audience proposée',
      'Modèle approprié',
      'Message FR / EN',
      'Canaux recommandés',
      'Communication prête pour validation',
    ],
  },
  program: {
    title: 'Une formation conçue pour vos opérations quotidiennes.',
    audience: 'Idéal pour les équipes Communications, Service aux citoyens, Opérations et administrateurs de la plateforme.',
    min: 'min',
    items: [
      { minutes: 10, title: 'Les nouveautés de Communications ATNA' },
      { minutes: 25, title: 'Formation pratique dans le module' },
      { minutes: 10, title: 'Démonstration ATNA sur un cas réel' },
      { minutes: 15, title: 'Questions et réponses avec l’équipe BCITI | ATNA' },
    ],
  },
  register: {
    title: 'Réservez votre place.',
    body: 'Venez découvrir comment utiliser Communications ATNA pour simplifier vos opérations et mieux rejoindre vos citoyens.',
    selected: 'Votre session',
    change: 'Changer de session',
    fields: {
      firstName: 'Prénom',
      lastName: 'Nom',
      organization: 'Organisation / Ville',
      role: 'Fonction',
      email: 'Courriel professionnel',
    },
    emailPrefix: 'Courriel',
    submit: 'Je m’inscris à la formation',
    submitting: 'Inscription en cours…',
    note: 'Vous recevrez une confirmation ainsi que le lien de connexion par courriel.',
    successTitle: 'Votre place est réservée.',
    successBody: 'Merci ! Une confirmation et le lien de connexion vous seront envoyés par courriel.',
    addToCalendar: 'Ajouter au calendrier (.ics)',
    googleCalendar: 'Google Agenda',
    error: 'L’inscription n’a pas pu être envoyée. Vérifiez vos informations et réessayez.',
    subject: 'Inscription — Formation Communications ATNA (session française)',
  },
  footer: {
    closing: ['Un meilleur accès pour les citoyens.', 'Plus de capacité pour les organisations.'],
    rights: '© 2026 BCITI Inc.',
    privacy: 'Politique de confidentialité',
    site: 'bciti.ai',
  },
}

export type WebinarCopy = typeof FR

const EN: WebinarCopy = {
  nav: {
    aria: 'Training navigation',
    home: 'BCITI | ATNA — Home',
    links: [
      { href: '#apprendre', label: 'Content' },
      { href: '#demonstration', label: 'Demo' },
      { href: '#programme', label: 'Agenda' },
    ],
    register: 'Register',
    langLabel: 'Language',
  },
  hero: {
    eyebrow: 'Client Training | Communications ATNA',
    titleLead: 'Master the new',
    titleAccent: 'Communications ATNA',
    promise: 'The right people. The right message. At the right time.',
    body: 'Join a hands-on 60-minute training session to discover the new Communications ATNA experience and learn how to better target, prepare, send and measure your communications with residents.',
    micro: 'Training reserved for BCITI | ATNA clients',
    duration: '60 minutes',
    format: 'Online',
    altQuestion: 'Prefer to attend in French?',
    altLink: 'View this session',
  },
  commandCenter: {
    module: 'Communications ATNA',
    status: 'Draft',
    title: 'Roadwork – Sector 3',
    stepsAria: 'Communication steps',
    steps: ['Audience', 'Message', 'Channels', 'Results'],
    audience: ['Sector 3', 'Residents subscribed to roadwork alerts'],
    audienceCount: '1,842 contacts',
    message: 'Roadwork is underway in sector 3. Traffic will be detoured until 6 PM.',
    channels: ['Push', 'SMS', 'Email', 'Voice'],
    results: [
      { label: 'Targeted', value: '1,842' },
      { label: 'Reached', value: '1,694' },
      { label: 'Not reached', value: '148' },
    ],
    illustrative: 'Illustrative data',
  },
  learn: {
    title: 'In 60 minutes, learn to get the most out of Communications ATNA.',
    cta: 'Register for the training',
    cards: [
      {
        icon: 'target',
        title: 'Target the right people',
        body: 'Build audiences by sector and neighbourhood, interest group, subscription, property, service, preference and other authorized data available in your environment.',
      },
      {
        icon: 'template',
        title: 'Communicate faster',
        body: 'Use pre-approved communication templates by topic, team and business rule to standardize and speed up your communications.',
      },
      {
        icon: 'atna',
        title: 'Work with ATNA',
        badge: 'ATNA plan option',
        body: 'Simply describe the situation. ATNA can help you prepare and improve the message, suggest the right audience, adapt the content to each channel and get the communication ready for approval.',
      },
      {
        icon: 'analytics',
        title: 'Measure what happens',
        body: 'Track your audiences, delivery and channel performance from one consolidated analytics view.',
      },
    ],
  },
  demo: {
    eyebrow: 'Live demo',
    title: 'From a situation in the field to a resident communication.',
    withAtna: 'With ATNA',
    prompt: 'Unplanned roadwork is affecting sector 3 until 6 PM. Prepare the communication for the affected residents.',
    quote: ['“', '”'],
    preparing: 'ATNA prepares the communication',
    governance: 'ATNA prepares. Your team approves and authorizes sending, according to your approval rules.',
    steps: [
      { label: 'Situation', text: '“Unplanned roadwork in sector 3”' },
      { label: 'Target', text: 'Identify the affected residents' },
      { label: 'Prepare', text: 'Use the right template and adapt the message' },
      { label: 'Send', text: 'Push · SMS · Email · Voice' },
      { label: 'Measure', text: 'See who was targeted, reached or not reached' },
    ],
    prepared: [
      'Suggested audience',
      'Appropriate template',
      'FR / EN message',
      'Recommended channels',
      'Communication ready for approval',
    ],
  },
  program: {
    title: 'A training session designed for your day-to-day operations.',
    audience: 'Ideal for Communications, Resident Services and Operations teams, and platform administrators.',
    min: 'min',
    items: [
      { minutes: 10, title: 'What’s new in Communications ATNA' },
      { minutes: 25, title: 'Hands-on training in the module' },
      { minutes: 10, title: 'ATNA demo on a real-world case' },
      { minutes: 15, title: 'Q&A with the BCITI | ATNA team' },
    ],
  },
  register: {
    title: 'Save your seat.',
    body: 'Discover how to use Communications ATNA to simplify your operations and better reach your residents.',
    selected: 'Your session',
    change: 'Change session',
    fields: {
      firstName: 'First name',
      lastName: 'Last name',
      organization: 'Organization / City',
      role: 'Job title',
      email: 'Professional email',
    },
    emailPrefix: 'Email',
    submit: 'Register for the training',
    submitting: 'Registering…',
    note: 'You’ll receive a confirmation and the meeting link by email.',
    successTitle: 'Your seat is reserved.',
    successBody: 'Thank you! A confirmation and the meeting link will be sent to you by email.',
    addToCalendar: 'Add to calendar (.ics)',
    googleCalendar: 'Google Calendar',
    error: 'Your registration could not be sent. Please check your information and try again.',
    subject: 'Registration — Communications ATNA Training (English session)',
  },
  footer: {
    closing: ['Better access for people.', 'More capacity for organizations.'],
    rights: '© 2026 BCITI Inc.',
    privacy: 'Privacy policy',
    site: 'bciti.ai',
  },
}

export const webinarCopy: Record<WebinarLang, WebinarCopy> = { FR, EN }
